import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { useMedia } from "use-media";
import { cx, media } from "@nju/css";
import { IOrder } from "@nju/data-access-portal-order";
import { Icon } from "@nju/icons";
import { salesRoutes } from "@nju/routes";
import { A, Button, DelayedReveal, Loader, Text } from "@nju/ui";
import {
  copyToClipboard,
  formatPhoneNumber,
  POSITIVE_DIGITS,
} from "@nju/utils";
import { formatAddress, formatDate } from "@nju/utils";
import {
  getItemsColor,
  mapOrderCancellationReason,
  mapOrderPaymentStatusName,
  mapOrderStatusName,
} from "../utils";
import { orderMachine } from "./order.machine";

interface IOrdersTableRowProps {
  order: IOrder;
}

export function OrdersTableRow({ order }: IOrdersTableRowProps) {
  const router = useRouter();

  const handleCopyToClipboard = React.useCallback(async (text: string) => {
    await copyToClipboard(text);
  }, []);

  const {
    id,
    mnp,
    status,
    processId,
    paymentStatus,
    appointment,
    adminUserId,
    dopUserId,
    subCategory,
    orderDate,
    installationAddress,
    delivery,
    waybillNumber,
    utechAgentUrl,
    cancellationReason,
    fulfillmentErrorDetails,
  } = order;

  const highlights = getItemsColor(
    status,
    paymentStatus,
    appointment?.startDate,
  );

  const [{ context, matches }, send] = useMachine(orderMachine, {
    context: {
      userId: adminUserId || dopUserId,
    },
    actions: {
      onClientCheckRedirect: () => {
        router.push(salesRoutes.statusKlienta.get());
      },
    },
  });

  const isMobile = useMedia(media.onlyMobile);
  const { clientData } = context;
  const isCancellation =
    status === "Cancelled" || status === "PendingCancellation";

  return (
    <div
      className="flex only-mobile:flex-col [&:not(:last-child)]:border-b-1 border-geyser"
      data-cy="sales-disposition-element"
    >
      <div
        className={cx(
          `flex items-center justify-center gap-4 py-2 from-tablet:py-3 from-tablet:gap-3 rounded-[8px] px-1 my-2 from-tablet:my-3 from-tablet:mr-4`,
          {
            "bg-ripeLemon": subCategory === "Ftth",
            "bg-cerulean": subCategory === "Mobile",
          },
        )}
      >
        <Icon
          height={16}
          name={subCategory === "Ftth" ? "globe" : "sim-card"}
          tone={subCategory === "Ftth" ? "neutral" : "secondary"}
          width={16}
        />
      </div>
      <div className="grow">
        <div
          className={`flex only-mobile:flex-col gap-4 py-2 from-tablet:gap-3 pt-3 w-full mr-4`}
        >
          <div className="from-tablet:min-w-[24%]">
            <Text display="block" size="s">
              numer zamówienia
            </Text>
            <div className="flex items-start">
              {id ? (
                <>
                  <Text size="s" testId="dop-number" weight="bold">
                    {id}
                  </Text>
                  <Button
                    display="inline"
                    onPress={() => {
                      handleCopyToClipboard(id);
                    }}
                    size="inline"
                    variant="simple"
                  >
                    <Icon className="flex items-center ml-1" name="copy" />
                  </Button>
                </>
              ) : (
                <Text display="block" size="s">
                  -
                </Text>
              )}
            </div>
          </div>
          <div className="from-tablet:w-[24%]">
            <Text display="block" size="s">
              data zamówienia
            </Text>
            {orderDate ? (
              <Text display="block" size="s" weight="bold">
                {formatDate(orderDate, false)}
              </Text>
            ) : (
              <Text display="block" size="s">
                -
              </Text>
            )}
          </div>
          <div className="from-tablet:w-[24%]">
            <Text display="block" size="s">
              stan zamówienia
            </Text>
            {status ? (
              <Text
                display="flex"
                size="s"
                tone={highlights.installationStatusColor}
                weight="bold"
              >
                {mapOrderStatusName(status)}
              </Text>
            ) : (
              <Text display="block" size="s">
                -
              </Text>
            )}
          </div>
          <div className="from-tablet:w-[24%]">
            <Text display="block" size="s">
              status płatności
            </Text>
            {paymentStatus ? (
              <Text
                className="items-center gap-2"
                display="flex"
                size="s"
                tone={highlights.paymentStatusColor}
                weight="bold"
              >
                {mapOrderPaymentStatusName(paymentStatus)}
                {highlights.paymentStatusColor === "alert" && (
                  <Icon name="exclamation-circle" tone="alert" />
                )}
              </Text>
            ) : (
              <Text
                className="items-center gap-2"
                display="flex"
                size="s"
                tone="alert"
                weight="bold"
              >
                nierozpoczęta
                <Icon name="exclamation-circle" tone="alert" />
              </Text>
            )}
          </div>
        </div>
        {(status === "Cancelled" ||
          status === "PendingCancellation" ||
          (processId === "MNP_IN" &&
            (mnp?.state === "Failed" || mnp?.state === "Cancelled"))) && (
          <div
            className={`flex only-mobile:flex-col gap-4 py-2 from-tablet:gap-3 w-full mr-4`}
          >
            <div className="from-tablet:w-[24%]">
              <Text display="block" size="s">
                powód anulowania
              </Text>
              {cancellationReason ? (
                <Text
                  display="block"
                  size="s"
                  testId="cancellation-reason"
                  tone="alert"
                  weight="bold"
                >
                  {mapOrderCancellationReason(cancellationReason)}
                </Text>
              ) : (
                <Text className="pt-1" display="block" size="s" tone="alert">
                  -
                </Text>
              )}
            </div>
            <div data-cy="cancellation-reason-details">
              <Text display="block" size="s">
                szczegóły anulowania
              </Text>
              {isCancellation && (
                <>
                  {fulfillmentErrorDetails?.mappedMessage ? (
                    <Text
                      breakLongValue="force"
                      display="block"
                      size="s"
                      testId="cancellation-reason-details"
                      tone="alert"
                      weight="bold"
                    >
                      {fulfillmentErrorDetails.mappedMessage}
                    </Text>
                  ) : (
                    <Text
                      className="pt-1"
                      display="block"
                      size="s"
                      testId="cancellation-reason-details"
                      tone="alert"
                    >
                      -
                    </Text>
                  )}
                  {fulfillmentErrorDetails?.additionalMappedMessage && (
                    <Text
                      breakLongValue="force"
                      display="block"
                      size="s"
                      testId="cancellation-reason-additional-details"
                    >
                      {fulfillmentErrorDetails.additionalMappedMessage}
                    </Text>
                  )}
                </>
              )}
              {processId === "MNP_IN" &&
                (mnp?.state === "Failed" || mnp?.state === "Cancelled") && (
                  <>
                    {mnp.reasonDescription && (
                      <Text
                        breakLongValue="force"
                        className="pt-1"
                        display="block"
                        size="s"
                        testId="cancellation-reason-details"
                        tone="alert"
                        weight="bold"
                      >
                        {mnp.reasonDescription}
                      </Text>
                    )}
                    {mnp.customerMessage && (
                      <Text
                        display="block"
                        size="s"
                        testId="cancellation-reason-additional-details"
                      >
                        {mnp.customerMessage}
                      </Text>
                    )}
                  </>
                )}
            </div>
          </div>
        )}
        {(subCategory === "Ftth" ||
          (subCategory === "Mobile" &&
            (delivery?.deliveryType === "BLISTER" ||
              delivery?.deliveryType === "COURIER"))) && (
          <div
            className={`flex only-mobile:flex-col gap-4 py-2 from-tablet:gap-3 w-full mr-4`}
          >
            {subCategory === "Ftth" && (
              <div className="from-tablet:w-[24%]">
                <Text display="block" size="s">
                  data instalacji
                </Text>
                {appointment ? (
                  <Text
                    className="items-center gap-2"
                    display="flex"
                    size="s"
                    tone={highlights.installationDateColor}
                    weight="bold"
                  >
                    {formatDate(appointment.startDate, false)}
                    {highlights.installationDateColor === "alert" && (
                      <Icon name="exclamation-circle" tone="alert" />
                    )}
                  </Text>
                ) : (
                  <Text display="block" size="s">
                    -
                  </Text>
                )}
              </div>
            )}
            {subCategory === "Ftth" && (
              <div className="from-tablet:w-[24%]">
                <Text display="block" size="s">
                  budynek jednorodzinny
                </Text>
                <Text
                  className="items-center gap-2"
                  display="flex"
                  size="s"
                  weight="bold"
                >
                  {installationAddress?.isSfh ? "tak" : "nie"}
                </Text>
              </div>
            )}
            {subCategory === "Ftth" && (
              <div>
                <Text display="block" size="s">
                  adres instalacji
                </Text>
                {installationAddress ? (
                  <>
                    <Text
                      display="block"
                      size="s"
                      textTransform="uppercase"
                      weight="bold"
                    >
                      {formatAddress(
                        {
                          street: installationAddress.street,
                          streetNumber: installationAddress.streetNumber,
                          municipality: installationAddress.municipality,
                          apartmentNumber: installationAddress.apartmentNumber,
                          postCode: installationAddress.postCode,
                        },
                        true,
                      )}
                    </Text>
                  </>
                ) : (
                  <Text display="block" size="s" weight="bold">
                    -
                  </Text>
                )}
              </div>
            )}
            {subCategory === "Mobile" && delivery?.deliveryType !== "PREOWNED" && (
              <div className="from-tablet:w-[24%]">
                <Text display="block" size="s">
                  sposób dostawy
                </Text>
                <Text display="block" size="s" weight="bold">
                  {delivery?.deliveryType === "COURIER" && "wysyłka kurierem"}
                  {delivery?.deliveryType === "BLISTER" && "odbiór osobisty"}
                </Text>
              </div>
            )}
            {subCategory === "Mobile" && delivery?.deliveryType === "COURIER" && (
              <div>
                <Text display="block" size="s">
                  adres dostawy
                </Text>
                {delivery && delivery.street ? (
                  <>
                    <Text
                      display="block"
                      size="s"
                      textTransform="uppercase"
                      weight="bold"
                    >
                      {formatAddress(
                        {
                          street: delivery.street,
                          streetNumber: delivery.streetNumber,
                          municipality: delivery.municipality,
                          apartmentNumber: delivery.apartmentNumber,
                          postCode: delivery.postCode,
                        },
                        true,
                      )}
                    </Text>
                  </>
                ) : (
                  <Text display="block" size="s" weight="bold">
                    -
                  </Text>
                )}
                {waybillNumber && (
                  <Link
                    href={`https://tt.dpd.com.pl/${waybillNumber}`}
                    passHref={true}
                    target="_blank"
                  >
                    <A
                      className="flex justify-start"
                      size="inline"
                      variant="neutral"
                    >
                      <Text className="w-full" size="s" withUnderline={true}>
                        śledź dostawę
                      </Text>
                    </A>
                  </Link>
                )}
              </div>
            )}
            {processId === "MNP_IN" &&
              mnp?.state !== "Failed" &&
              mnp?.state !== "Cancelled" &&
              mnp?.transferDate && (
                <div>
                  <Text display="block" size="s">
                    planowana data przeniesienia
                  </Text>
                  <Text
                    className="items-center gap-2"
                    display="flex"
                    size="s"
                    tone="accent"
                    weight="bold"
                  >
                    {formatDate(mnp.transferDate, false)}
                  </Text>
                </div>
              )}
          </div>
        )}
        <div className="flex only-mobile:flex-col gap-4 py-2 from-tablet:gap-3 pb-3 w-full mr-4">
          {matches("visible") ? (
            <div className="w-full relative min-h-[49px]">
              <div className="absolute right-0 from-tablet:top-0 only-mobile:bottom-0">
                <Button
                  display="inline"
                  onPress={() => {
                    return send({ type: "HIDE" });
                  }}
                  size="inline"
                  testId="hide-client-details-button"
                  variant="simple"
                >
                  <Icon height={18} name="arrow-from-right" width={22} />
                </Button>
              </div>
              {matches("visible.error") && (
                <div className="flex gap-3 only-mobile:flex-row-reverse only-mobile:pb-1">
                  <div>
                    <Button
                      display="inline"
                      onPress={() => {
                        return send({ type: "REFRESH" });
                      }}
                      size="inline"
                      variant="simple"
                    >
                      <Icon height={24} name="sync" width={24} />
                    </Button>
                  </div>
                  <div className="w-full">
                    <Text display="block" size="s">
                      Ups...
                    </Text>
                    <Text
                      display={isMobile ? "block" : "inline"}
                      size="s"
                      weight="bold"
                    >
                      Nie udało się pobrać danych.{" "}
                    </Text>
                    <Text
                      display={isMobile ? "block" : "inline"}
                      size="s"
                      weight="bold"
                    >
                      Spróbuj ponownie
                    </Text>
                  </div>
                </div>
              )}
              {(matches("visible.loading") ||
                matches("visible.checkClient")) && (
                <div className="absolute z-[1] -translate-y-2/4	-translate-x-2/4 left-2/4 top-2/4">
                  <Loader tone="dark" />
                </div>
              )}
              {matches("visible.ready") && (
                <div className="flex gap-3 only-mobile:flex-col">
                  <div className="from-tablet:w-[24%] flex gap-3 only-mobile:justify-between only-mobile:flex-row-reverse">
                    <div>
                      <Button
                        display="inline"
                        onPress={() => {
                          return send({ type: "CHECK_CLIENT" });
                        }}
                        size="inline"
                        testId="client-dashboard-button"
                        variant="simple"
                      >
                        <Icon height={24} name="user-circle" width={24} />
                      </Button>
                    </div>
                    <div className="only-mobile:w-[80%]">
                      <Text display="block" size="s">
                        dane klienta
                      </Text>
                      <Text
                        breakLongValue="force"
                        display="block"
                        size="s"
                        weight="bold"
                      >
                        {clientData.firstName} {clientData.lastName}
                      </Text>
                    </div>
                  </div>
                  <div className="only-mobile:w-[80%]">
                    <Text
                      breakLongValue="force"
                      display="block"
                      size="s"
                      tone="accent"
                    >
                      {clientData.email}
                    </Text>
                    <Text
                      breakLongValue="force"
                      display="block"
                      size="s"
                      weight="bold"
                    >
                      {isMobile && clientData.attributes?.contactNumber && (
                        <Link
                          href={`tel:${clientData.attributes.contactNumber[0]}`}
                        >
                          {formatPhoneNumber(
                            clientData.attributes.contactNumber[0],
                            POSITIVE_DIGITS,
                            " ",
                            undefined,
                            true,
                          )}
                        </Link>
                      )}
                      {!isMobile &&
                        clientData.attributes.contactNumber &&
                        formatPhoneNumber(
                          clientData.attributes.contactNumber[0],
                          POSITIVE_DIGITS,
                          " ",
                          undefined,
                          true,
                        )}
                    </Text>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex only-mobile:flex-col items-center gap-3 w-full">
              <div className="from-tablet:w-[50%] w-full">
                <Button
                  display="block"
                  onPress={() => {
                    return send({ type: "SHOW" });
                  }}
                  size="inline"
                  testId="show-client-details-button"
                  variant="simple"
                >
                  <Text
                    align="center"
                    className="whitespace-nowrap border-2 border-daintree rounded-[60px] py-[12px] px-6 w-full cursor-pointer"
                    display="block"
                  >
                    pokaż dane klienta
                  </Text>
                </Button>
              </div>
              {subCategory === "Ftth" && orderDate && utechAgentUrl && (
                <div className="from-tablet:w-[50%] w-full">
                  <DelayedReveal from={orderDate} wait={600_000}>
                    <Link href={utechAgentUrl} passHref={true} target="_blank">
                      <A
                        className="text-daintree underline"
                        size="inline"
                        variant="neutral"
                      >
                        <Text>przejdź do UTECH</Text>
                      </A>
                    </Link>
                  </DelayedReveal>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
